import { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Header from './components/Header'
import Hero from './components/Hero'
import Services from './components/Services'
import Clients from './components/Clients'
import About from './components/About'
import AboutUs from './components/AboutUs'
import AboutUsNew from './components/AboutUsNew'
import LoadingIndicator from './components/LoadingIndicator'
import Contact from './components/Contact'
import ContactPage from './pages/ContactPage'
import Footer from './components/Footer'
import AllServices from './components/AllServices'
import SEO from './components/SEO'
import { Construction, PowerGeneration, Environmental, OilFuelSupply, Logistics, GeneralTrading, Recycling } from './services'

const PageTransition = ({ children }) => {
  return children
}

const ScrollToTop = () => {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      // Small timeout to ensure content is rendered
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    } else {
      window.scrollTo(0, 0)
    }
  }, [pathname, hash])

  return null
}


const LoadingOverlay = () => {
  return (
    <motion.div
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      exit={{ scaleY: 0 }}
      transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="fixed inset-0 z-[9999] bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl flex items-center justify-center"
      style={{ transformOrigin: 'bottom' }}
    >
      <LoadingIndicator size="large" showText={true} />
    </motion.div>
  )
}

function App() {
  const [currentSection, setCurrentSection] = useState('hero')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Enhanced smooth scrolling with morphing transition
    const handleSmoothScroll = async (e) => {
      const href = e.target.getAttribute('href')
      if (href && href.startsWith('#')) {
        e.preventDefault()

        // Start transition
        setIsTransitioning(true)

        // Wait for transition overlay
        setTimeout(() => {
          const element = document.querySelector(href)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
            setCurrentSection(href.substring(1))
          }

          // End transition
          setTimeout(() => {
            setIsTransitioning(false)
          }, 400)
        }, 300)
      }
    }

    // Add event listeners to all anchor links
    const links = document.querySelectorAll('a[href^="#"]')
    links.forEach(link => {
      link.addEventListener('click', handleSmoothScroll)
    })

    // Cleanup
    return () => {
      links.forEach(link => {
        link.removeEventListener('click', handleSmoothScroll)
      })
    }
  }, [])

  // Handle route changes with loading
  useEffect(() => {
    const handleRouteChange = () => {
      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
      }, 800)
    }

    // Simulate route change detection
    const originalPushState = history.pushState
    history.pushState = function (...args) {
      handleRouteChange()
      return originalPushState.apply(this, args)
    }

    return () => {
      history.pushState = originalPushState
    }
  }, [])

  const overlayVariants = {
    initial: {
      scaleY: 0,
      transformOrigin: "bottom"
    },
    animate: {
      scaleY: 1,
      transformOrigin: "bottom",
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    exit: {
      scaleY: 0,
      transformOrigin: "top",
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20,
      scale: 0.98
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 1.02,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }

  const HomePage = () => (
    <>
      <SEO
        title="AL-KONDA Group | Premier Engineering & Infrastructure Solutions in Iraq & Kurdistan"
        description="AL-KONDA Group delivers world-class construction, power generation, environmental services, logistics, and infrastructure solutions across Iraq and Kurdistan Region since 2011."
        keywords="construction Iraq, power generation Kurdistan, environmental services, logistics Iraq, general trading, infrastructure development, engineering services, Erbil, AL-KONDA"
      />
      <Hero />
      <Services />
      <Clients />
      <About />
    </>
  )

  const AnimatedRoutes = () => {
    return (
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutUsNew />} />
        <Route path="/about-old" element={<AboutUs />} />
        <Route path="/services" element={<AllServices />} />
        <Route path="/services/construction" element={<Construction />} />
        <Route path="/services/power-generation" element={<PowerGeneration />} />
        <Route path="/services/environmental" element={<Environmental />} />
        <Route path="/services/oil-fuel-supply" element={<OilFuelSupply />} />
        <Route path="/services/logistics" element={<Logistics />} />
        <Route path="/services/general-trading" element={<GeneralTrading />} />
        <Route path="/services/recycling" element={<Recycling />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    )
  }

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen overflow-x-hidden relative">
        <Header />

        <div className="relative z-10">
          <AnimatedRoutes />
        </div>

        <Footer />

        {/* Loading Overlay */}
        <AnimatePresence>
          {isLoading && <LoadingOverlay />}
        </AnimatePresence>

        {/* Morphing Transition Overlay */}
        <AnimatePresence>
          {isTransitioning && (
            <motion.div
              variants={overlayVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="fixed inset-0 z-50 bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl flex items-center justify-center"
            >
              <LoadingIndicator size="medium" showText={false} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Router>
  )
}

export default App